import { api, zeusApi } from 'src/store/api';
import { type PaginationRequestData, type ResponseWithPaginationMeta } from './types';
import { type FlexflowApplicationType, type FlexflowApplication } from '../types/flexflow';
import {
  type TRANSACTION_CATEGORY,
  type FlexflowTransaction,
  type TRANSACTION_KIND,
  type TRANSACTION_STATUS,
  type TRANSACTION_ORIGIN
} from 'src/modules/Flexflow/History/types';
import moment from 'moment';
import { DATE_FORMAT } from 'src/modules/Flexflow/History/constants';
import {
  type FlexflowCardStatus,
  type ActionButtonValues,
  type FlexflowEmployeeStatuses
} from 'src/modules/Flexflow/types';
import { type FlexflowEmployeeDataToDisplay } from 'src/modules/Flexflow/Contracts/types';
import { skipEmpty } from './utils';
import { type Employee } from 'src/shared/types/employee';
import { type FlexflowEmployeeStatusesBeforeActivation } from 'src/modules/Flexflow/Contracts/ContractStatusBeforeInvitation';
import { FlexflowCardDisplayedStatus } from 'src/modules/Flexflow/Contracts/FlexflowContractListActions/hooks';
import { type FlexflowTransactionsListFilter } from 'src/modules/Flexflow/History/TransactionsListActions/hooks';

const getApplicationsByType = (applications: FlexflowApplication[]) => (type: FlexflowApplicationType) =>
  applications.filter((application: FlexflowApplication) => application.type === type);

type GetFlexflowContractsArg = {
  pagination: PaginationRequestData;
  filterValues?: {
    searchTerm?: string;
    filters?: {
      status?: FlexflowEmployeeStatuses[] | FlexflowEmployeeStatusesBeforeActivation;
      cardStatus?: FlexflowCardDisplayedStatus[];
      cardSuspendedBySwan?: boolean;
      frWalletOn?: 'true' | 'false';
      sbAssigned?: 'true' | 'false';
    };
  } | null;
};

type GetFlexflowTransactionsArg = {
  pagination: PaginationRequestData;
  filterValues?: {
    searchValue?: string;
    startDate?: string;
    endDate?: string;
    filters?: {
      status?: TRANSACTION_STATUS | '';
      category?: TRANSACTION_CATEGORY | '';
      transactionKind?: TRANSACTION_KIND | '';
      transactionOrigin?: TRANSACTION_ORIGIN | '';
    };
  } | null;
};

type GetFlexflowApplicationsArg = {
  type: FlexflowApplicationType;
  companyId: number;
};

type GetFlexflowContractArg = {
  id: number;
};

type GetFlexflowApplicationById = {
  uuid: string;
};

type PutUpdateFlexflowEmployeesBulkResponse = {
  updatedEmployees: string[];
};

type PutUpdateFlexflowEmployeesBulk = {
  employeeIds: number[];
  data: {
    invitationStatus?: ActionButtonValues;
    sbConfig?: Array<{
      employeeId: number;
      sbFoodAccrual?: number;
      sbTransportAccrual?: number;
    }>;
  };
};

type PutUpdateFlexflowCardStatus = {
  employeeId: number;
  status: FlexflowCardStatus;
  cardMedium: 'physical' | 'virtual';
};

type NovationContract = {
  signedURL: string;
};

type PutUpdateFlexflowCompanySettings = {
  foodBenefitEnabled?: boolean;
  transportBenefitEnabled?: boolean;
  foodLimit?: number;
  transportLimit?: number;
};

type FlexflowCompany = {
  companyId: number;
  foodBenefitEnabled?: boolean;
  transportBenefitEnabled?: boolean;
  foodLimit?: number;
  transportLimit?: number;
};

export type SocialBenefitEmployee = {
  employeeId: number;
  fullName: string;
  personalId: string;
  payrollId: string;
  sbFoodAccrual: number;
  sbTransportAccrual: number;
};

export type GetEmployeesSocialBenefitAccrualsResponse = {
  employees: SocialBenefitEmployee[];
  total: number;
};

type PutUpdateSocialBenefit = {
  employeeId: number;
  sbFoodAccrual?: number;
  sbTransportAccrual?: number;
};

type SocialBenefitBulkIdentifiers = {
  personalId: string;
  payrollId: string;
};

export type SocialBenefitBulkSuccessRow = {
  employeeId: number;
  sbFoodAccrual: number;
  sbTransportAccrual: number;
  identifiers: SocialBenefitBulkIdentifiers;
  sheetIndex: number;
  rowNumber: number;
};

type SocialBenefitBulkErrorRow = {
  identifiers: SocialBenefitBulkIdentifiers;
  sheetIndex: number;
  rowNumber: number;
  reason: string;
};

export type SocialBenefitBulkOriginResponse = {
  success: SocialBenefitBulkSuccessRow[];
  error: SocialBenefitBulkErrorRow[];
  notFound: SocialBenefitBulkErrorRow[];
};

export type SocialBenefitBulkResponse = {
  success: SocialBenefitBulkSuccessRow[];
  error: SocialBenefitBulkErrorRow[];
};

export type GenerateSocialBenefitBulkPreviewFromFileArg = {
  file: File;
};

export type ExternalConsumptionAmounts = {
  food: number;
  transport: number;
  nursery: number;
  education: number;
  healthInsurance: number;
  other: number;
};

export type ExternalConsumptionBulkSuccessRow = ExternalConsumptionAmounts & {
  employeeId: number;
};

export type ExternalConsumptionBulkReason = {
  code: string;
  params?: Record<string, string>;
};

export type ExternalConsumptionCellValues = {
  food: string;
  transport: string;
  nursery: string;
  education: string;
  healthInsurance: string;
  other: string;
};

export type ExternalConsumptionBulkErrorRow = {
  personalId: string;
  reason: ExternalConsumptionBulkReason;
  values: ExternalConsumptionCellValues;
};

export type ExternalConsumptionBulkResponse = {
  success: ExternalConsumptionBulkSuccessRow[];
  error: ExternalConsumptionBulkErrorRow[];
};

export type GenerateExternalConsumptionBulkPreviewFromFileArg = {
  file: File;
};

export type ApplyExternalConsumptionBulkArg = {
  externalConsumption: Array<ExternalConsumptionAmounts & { employeeId: number }>;
};

export type ExternalConsumptionListItem = ExternalConsumptionAmounts & {
  employeeId: number;
  fullName: string;
  personalId: string;
  payrollId: string;
  year: number;
  total: number;
  updatedAt: string;
};

export type GetExternalConsumptionArg = {
  page: number;
  size: number;
  search?: string;
};

export type GetExternalConsumptionResponse = ResponseWithPaginationMeta<ExternalConsumptionListItem>;

const withoutCardStatus = ({
  cardStatus,
  ...rest
}: {
  status?: FlexflowEmployeeStatuses[] | FlexflowEmployeeStatusesBeforeActivation;
  cardStatus?: FlexflowCardDisplayedStatus[];
  cardSuspendedBySwan?: boolean;
}) => ({ ...rest });

const mapFlexflowContractsFilters = (arg: GetFlexflowContractsArg) => {
  const withoutEmpty = skipEmpty({
    searchTerm: arg.filterValues?.searchTerm,
    status: arg.filterValues?.filters?.status,
    cardStatus: arg.filterValues?.filters?.cardStatus,
    frWalletOn: arg.filterValues?.filters?.frWalletOn,
    sbAssigned: arg.filterValues?.filters?.sbAssigned
  });

  if (withoutEmpty.cardStatus === FlexflowCardDisplayedStatus.ACTIVE) {
    return {
      ...withoutEmpty,
      employeeStatus: 'active'
    };
  }

  if (withoutEmpty.cardStatus === FlexflowCardDisplayedStatus.UNACTIVATED) {
    return {
      ...withoutEmpty,
      employeeStatus: 'active'
    };
  }

  if (withoutEmpty.cardStatus === FlexflowCardDisplayedStatus.FROZEN) {
    return {
      ...withoutEmpty,
      employeeStatus: 'active',
      cardStatus: FlexflowCardDisplayedStatus.SUSPENDED
    };
  }

  if (withoutEmpty.cardStatus === FlexflowCardDisplayedStatus.SUSPENDED) {
    return {
      ...withoutCardStatus(withoutEmpty),
      employeeStatus: ['on_leave', 'inactive']
    };
  }

  return {
    ...withoutEmpty
  };
};

const extendedApi = {
  ...api.injectEndpoints({
    endpoints: build => ({
      getFlexflowTransactions: build.query<ResponseWithPaginationMeta<FlexflowTransaction>, GetFlexflowTransactionsArg>(
        {
          query: arg => {
            const filterParams = skipEmpty({
              searchValue: arg.filterValues?.searchValue,
              status: arg.filterValues?.filters?.status,
              category: arg.filterValues?.filters?.category,
              transactionKind: arg.filterValues?.filters?.transactionKind,
              transactionOrigin: arg.filterValues?.filters?.transactionOrigin,
              startDate: arg.filterValues?.startDate && moment(arg.filterValues.startDate).format(DATE_FORMAT),
              endDate: arg.filterValues?.endDate && moment(arg.filterValues.endDate).format(DATE_FORMAT)
            });

            return {
              url: `flexflow/payroll`,
              params: {
                ...filterParams,
                page: arg.pagination.page + 1,
                size: arg.pagination.perPage
              }
            };
          },
          providesTags: ['Flexflow']
        }
      ),
      getFlexflowContracts: build.query<
        ResponseWithPaginationMeta<FlexflowEmployeeDataToDisplay>,
        GetFlexflowContractsArg
      >({
        query: arg => {
          const filterParams = mapFlexflowContractsFilters(arg);

          return {
            url: `flexflow/employees`,
            params: {
              ...filterParams,
              size: arg.pagination.perPage,
              page: arg.pagination.page
            }
          };
        },
        providesTags: ['Flexflow']
      }),
      getFlexflowInvitedContracts: build.query<ResponseWithPaginationMeta<Employee>, GetFlexflowContractsArg>({
        query: arg => {
          const filterParams = skipEmpty({
            searchTerm: arg.filterValues?.searchTerm,
            status: arg.filterValues?.filters?.status,
            cardStatus: arg.filterValues?.filters?.cardStatus,
            frWalletOn: arg.filterValues?.filters?.frWalletOn,
            sbAssigned: arg.filterValues?.filters?.sbAssigned
          });

          return {
            url: `flexflow/employees/invited`,
            params: {
              ...filterParams,
              size: arg.pagination.perPage,
              page: arg.pagination.page
            }
          };
        },
        providesTags: ['Flexflow']
      }),
      updateFlexflowEmployeesBulk: build.mutation<
        PutUpdateFlexflowEmployeesBulkResponse,
        PutUpdateFlexflowEmployeesBulk
      >({
        query: arg => ({
          url: `flexflow/employees`,
          method: 'PUT',
          body: arg
        }),
        invalidatesTags: ['Flexflow', 'SocialBenefit']
      }),
      getFlexflowContract: build.query<FlexflowEmployeeDataToDisplay, GetFlexflowContractArg>({
        query: arg => `flexflow/employees/${arg.id}`,
        providesTags: ['Flexflow']
      }),
      updateCardStatus: build.mutation<PutUpdateFlexflowEmployeesBulkResponse, PutUpdateFlexflowCardStatus>({
        query: arg => ({
          url: `flexflow/employees/${arg.employeeId}/card/status`,
          method: 'PUT',
          body: { status: arg.status, cardMedium: arg.cardMedium }
        }),
        invalidatesTags: ['Flexflow']
      }),
      terminateCard: build.mutation<PutUpdateFlexflowEmployeesBulkResponse, Partial<PutUpdateFlexflowCardStatus>>({
        query: arg => ({
          url: `flexflow/employees/${arg.employeeId}/card/terminate`,
          method: 'POST',
          body: { cardMedium: arg.cardMedium }
        }),
        invalidatesTags: ['Flexflow']
      }),
      postGenerateHistoryFile: build.mutation<
        void,
        { filterValues: FlexflowTransactionsListFilter; emailAddress: string }
      >({
        query: arg => {
          const filterParams = skipEmpty({
            searchValue: arg?.filterValues?.searchValue,
            status: arg?.filterValues?.filters?.status,
            category: arg?.filterValues?.filters?.category,
            transactionKind: arg?.filterValues?.filters?.transactionKind,
            transactionOrigin: arg?.filterValues?.filters?.transactionOrigin,
            startDate: arg?.filterValues?.startDate && moment(arg?.filterValues.startDate).format(DATE_FORMAT),
            endDate: arg?.filterValues?.endDate && moment(arg?.filterValues.endDate).format(DATE_FORMAT)
          });

          return {
            url: `flexflow/payroll/file`,
            method: 'POST',
            body: {
              ...filterParams,
              email: arg.emailAddress
            }
          };
        },
        invalidatesTags: ['Flexflow']
      }),
      getNovationUrl: build.query<NovationContract, { employeeId: string }>({
        query: arg => `/employee/${arg.employeeId}/novation-contract`,
        providesTags: ['Flexflow']
      }),
      updateFlexflowCompanySettings: build.mutation<FlexflowCompany, PutUpdateFlexflowCompanySettings>({
        query: arg => ({
          url: `flexflow/companies`,
          method: 'PUT',
          body: arg
        }),
        invalidatesTags: ['Flexflow', 'SocialBenefit']
      }),
      getEmployeesSocialBenefitAccruals: build.query<
        GetEmployeesSocialBenefitAccrualsResponse,
        { search?: string; page: number; size: number } | void
      >({
        query: arg => ({
          url: 'flexflow/companies/social-benefit',
          params: arg
            ? {
                search: arg.search,
                page: arg.page + 1,
                size: arg.size
              }
            : undefined
        }),
        transformResponse: (
          response: ResponseWithPaginationMeta<SocialBenefitEmployee>
        ): GetEmployeesSocialBenefitAccrualsResponse => ({
          employees: response.data,
          total: response.meta.total
        }),
        providesTags: ['SocialBenefit']
      }),
      updateSocialBenefit: build.mutation<void, PutUpdateSocialBenefit>({
        query: ({ employeeId, ...body }) => ({
          url: `flexflow/employees/${employeeId}`,
          method: 'PUT',
          body
        }),
        invalidatesTags: ['Flexflow']
      }),
      generateSocialBenefitBulkPreviewFromFile: build.mutation<
        SocialBenefitBulkResponse,
        GenerateSocialBenefitBulkPreviewFromFileArg
      >({
        query: arg => ({
          url: `flexflow/social-benefit/generate-bulk-preview`,
          method: 'POST',
          headers: {
            'Content-type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          body: arg.file
        }),
        transformResponse: (response: SocialBenefitBulkOriginResponse) => {
          return { success: response.success, error: [...response?.error, ...response?.notFound] };
        },
        invalidatesTags: ['SocialBenefit']
      }),
      getExternalConsumption: build.query<GetExternalConsumptionResponse, GetExternalConsumptionArg>({
        query: arg => ({
          url: 'flexflow/external-consumption',
          params: skipEmpty({
            search: arg.search,
            page: arg.page + 1,
            size: arg.size
          })
        }),
        providesTags: ['ExternalConsumption']
      }),
      generateExternalConsumptionBulkPreviewFromFile: build.mutation<
        ExternalConsumptionBulkResponse,
        GenerateExternalConsumptionBulkPreviewFromFileArg
      >({
        query: arg => ({
          url: `flexflow/external-consumption/generate-bulk-preview`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          body: arg.file
        })
      }),
      applyExternalConsumptionBulk: build.mutation<void, ApplyExternalConsumptionBulkArg>({
        query: arg => ({
          url: `flexflow/external-consumption`,
          method: 'POST',
          body: arg
        }),
        invalidatesTags: ['Flexflow', 'ExternalConsumption']
      })
    })
  }),
  ...zeusApi.injectEndpoints({
    endpoints: build => ({
      getFlexflowApplications: build.query<ResponseWithPaginationMeta<FlexflowApplication>, GetFlexflowApplicationsArg>(
        {
          async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
            const allApplications = await fetchWithBQ({
              url: `holding/flexflow/application`
            });

            if (allApplications.error) {
              return { error: allApplications.error };
            }
            const applicationsByType: FlexflowApplication[] = getApplicationsByType(
              allApplications?.data as ResponseWithPaginationMeta<FlexflowApplication>['data']
            )(arg.type);

            return {
              data: {
                data: applicationsByType,
                meta: allApplications.meta
              } as unknown as ResponseWithPaginationMeta<FlexflowApplication>
            };
          },
          providesTags: ['Flexflow']
        }
      ),
      getFlexflowApplicationById: build.query<FlexflowApplication, GetFlexflowApplicationById>({
        query: arg => `/holding/flexflow/application/${arg.uuid}`,
        providesTags: ['Flexflow']
      })
    })
  })
};

export const {
  useGetFlexflowTransactionsQuery,
  useGetFlexflowApplicationsQuery,
  useGetFlexflowApplicationByIdQuery,
  useGetFlexflowContractsQuery,
  useGetFlexflowInvitedContractsQuery,
  useUpdateFlexflowEmployeesBulkMutation,
  usePostGenerateHistoryFileMutation,
  useUpdateCardStatusMutation,
  useGetFlexflowContractQuery,
  useTerminateCardMutation,
  useGetNovationUrlQuery,
  useUpdateFlexflowCompanySettingsMutation,
  useGetEmployeesSocialBenefitAccrualsQuery,
  useLazyGetEmployeesSocialBenefitAccrualsQuery,
  useUpdateSocialBenefitMutation,
  useGenerateSocialBenefitBulkPreviewFromFileMutation,
  useGenerateExternalConsumptionBulkPreviewFromFileMutation,
  useApplyExternalConsumptionBulkMutation,
  useGetExternalConsumptionQuery
} = extendedApi;
